<template>
  <div>
    <!-- In new mode the judgment-selection step is rendered by cassation-request-switcher, above the variant
         boundary, so a variant is never on screen before the judgment that resolves it has been chosen. -->
    <naj-box
      v-if="!isNewMode"
      :title="local.caseRelatedRequest.cassationRequest.title"
    >
      <v-row>
        <v-col cols="6">
          <naj-input
            :type-id="formFieldType.selectbox"
            :value.sync="formModel.judgementCode"
            :label="local.caseRelatedRequest.cassationRequest.judgementCode"
            :items="judgments"
            item-value="judgementCode"
            item-text="judgementCode"
            validates="required"
            :disabled="isViewMode || isEditMode"
          >
          </naj-input>
        </v-col>
      </v-row>
    </naj-box>

    <naj-box
      v-if="formModel.judgementCode"
      :title="local.caseRelatedRequest.cassationRequest.reasons"
    >
      <template v-for="(reason,index) in formModel.cassationReasons">
        <v-row :key="index">
          <v-col cols="12">
            <naj-input
              :type-id="formFieldType.checkbox"
              :value.sync="reason.checked"
              :label="reason.label"
              :disabled="isViewMode"
              @change="reasonCheckChanged(index)"
            ></naj-input>
          </v-col>
          <v-col
            v-if="reason.checked"
            cols="6"
            lg="6"
          >
            <naj-input
              :type-id="formFieldType.textarea"
              :value.sync="reason.description"
              label=""
              :technical-name="'cassationReasonDescription' + reason.reasonId"
              :disabled="isViewMode"
              validates="required|max:2000|allowed_special_character"
              rows="4"
            ></naj-input>
          </v-col>
        </v-row>
      </template>
    </naj-box>

    <naj-box :title="local.caseRelatedRequest.cassationRequest.cassationRequestAttachment">
      <v-row>
        <naj-alert type="warning">
          <template slot="content">
            {{ local.caseRelatedRequest.cassationRequest.attachmentAlert }}
          </template>
        </naj-alert>
      </v-row>
      <v-row>
        <v-col
          v-if="formModel.cassationFile"
          cols="3"
        >
          <p class="objection-judgment-confirm-text">
            {{ local.caseRelatedRequest.cassationRequest.cassationRequestAttachment }}
          </p>
          <attachment-card
            :file="{ ...uploadedFile }"
            :show-delete-button="!isViewMode"
            @deleteAttachment="showDeleteObjectionNoteModal"
            @viewAttachment="viewAttachment"
          ></attachment-card>
        </v-col>
        <v-col
          v-else
          class="objection-note-attachment"
          cols="12"
        >
          <p class="objection-judgment-confirm-text blackColor">
            {{ local.caseRelatedRequest.cassationRequest.addCassationRequestAttachment }}
          </p>
          <load-local-attachment
            :max-file-size="5"
            :upload-disabled="isViewMode"
            @fileSelected="fileSelected"
          >
          </load-local-attachment>
        </v-col>
      </v-row>
    </naj-box>

    <case-judgement-modal
      :selected-judgement="selectedJudgment"
      :modal-visibility="showCaseJudgementModal"
      @modalClose="closeCaseJudgementModal"
    ></case-judgement-modal>

    <render-attachment-modal
      :attachment-file="uploadedFile"
      :modal-visibility="showViewAttachmentModal"
      @modalClose="hideViewAttachmentModal"
    >
    </render-attachment-modal>

    <naj-confirmation
      :show-modal="showDeleteObjectionNoteAttachment"
      color="error"
      :title="local.common.removeConfirmation"
      :message="local.caseRelatedRequest.VerdictObjectionRequest.removeObjectionNoteAttachmentMessage"
      :ok-btn-label="local.common.remove"
      @ok="confirmDeleteObjectionNote"
      @cancel="cancelDeleteObjectionNote"
    ></naj-confirmation>
    <naj-alert-modal
      :show-modal="showAlertModalForCriminalCases"
      type="information"
      :title="local.common.criminalAttachmentTitle"
      :message="local.common.criminalAttachment"
      @onCancelModal="showAlertModalForCriminalCases=false"
    >
    </naj-alert-modal>
  </div>
</template>

<script>
import { mode, socialIdentityTypeEnum, countriesEnum } from '@/helpers/enum';
import { createNamespacedHelpers } from 'vuex';
const { mapState: mapLUTState } = createNamespacedHelpers('LookupsModule');
const { mapState: mapGlobalState } = createNamespacedHelpers('GlobalModule');
const { mapActions } = createNamespacedHelpers('CasesModule');
import CaseJudgementModal from '../../../../cases/case-judgement-modal';
import LoadLocalAttachment from '../../../../../components/shared/attachment-modal/load-local-attachment';
import AttachmentCard from '@/components/shared/attachment-modal/attachment-card';
import RenderAttachmentModal from '@/components/shared/attachment-modal/render-attachment-modal';

export default {
  name: 'CassationRequestLegacy',
  inject: ['$validator'],
  components: {
    CaseJudgementModal,
    LoadLocalAttachment,
    AttachmentCard,
    RenderAttachmentModal,
  },
  props: {
    formModel: {
      type: Object,
      default: () => ({}),
    },
    requiredData: {
      type: Object,
      default: () => ({}),
    },
    loadedJudgments: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      judgments: [],
      reasons: [
        {
          reasonId: 71,
          checked: false,
          description: '',
          label: this.local.cassationObjectionReasons.oppositeOfIslamicLaw,
        },
        {
          reasonId: 72,
          checked: false,
          description: '',
          label: this.local.cassationObjectionReasons.badCourtHierarchy,
        },
        {
          reasonId: 73,
          checked: false,
          description: '',
          label: this.local.cassationObjectionReasons.courtOrCircleUnspecialized,
        },
        {
          reasonId: 74,
          checked: false,
          description: '',
          label: this.local.cassationObjectionReasons.notAccurateDescription,
        },
      ],

      showCaseJudgementModal: false,
      showDeleteObjectionNoteAttachment: false,
      showViewAttachmentModal: false,
      showAlertModalForCriminalCases: false,
      selectedJudgment: {},
      lastValidatedJudgementCode: null,
    };
  },
  computed: {
    ...mapLUTState(['cassationObjectionReasons']),
    ...mapGlobalState(['userInfo']),
    isViewMode() {
      return this.formModel && this.formModel.mode === mode.view;
    },
    isEditMode() {
      return this.formModel && this.formModel.mode === mode.update;
    },
    isNewMode() {
      return this.$route.params.requestObjectKey === 'new';
    },
    uploadedFile() {
      if (this.formModel.cassationFile && this.formModel.cassationFile.fileData) {
        const fileData = this.formModel.cassationFile.fileData;
        return {
          name: fileData.fileName,
          attachmentKey: fileData.attachmentKey,
          sharePointId: fileData.attachement,
          isImage: fileData.fileType.startsWith('image'),
          fileType: fileData.fileType,
        };
      }
      return {};
    },
    routeRequestObjectKey() {
      return this.$route.query.requestObjectKey;
    },
  },
  watch: {
    'formModel.judgementCode'(newVal, oldVal) {
      if (newVal === oldVal) {
        return;
      }

      if (this.isNewMode) {
        // ensure the static reasons list is always present in legacy mode
        if (!this.formModel.cassationReasons || !this.formModel.cassationReasons.length) {
          this.$set(this.formModel, 'cassationReasons', this.reasons);
        }
        // reset only user selections (checked state + description), not the list itself
        (this.formModel.cassationReasons || []).forEach((reason) => {
          this.$set(reason, 'checked', false);
          this.$set(reason, 'description', '');
        });
      }

      this.setSelectedJudgment();
    },
  },
  mounted() {
    if (this.formModel) {

      // only initialize reasons from lookup if creating new request and no persisted data
      if ((this.formModel.cassationReasons || []).length === 0 && this.isNewMode) {
        this.$set(this.formModel, 'cassationReasons', this.reasons);
      }

      // a prior mount of the New component (e.g. the user tried a New-flagged judgment before
      // switching to this Legacy-flagged one) may have left this stuck `true` on the shared
      // formModel; clear it so a request submitted under Legacy persists it as false (FR-005b).
      if (this.isNewMode && this.formModel.isUpdatedCassationRequest) {
        this.$set(this.formModel, 'isUpdatedCassationRequest', false);
      }

      if (this.formModel.judgementCode) {
        this.setSelectedJudgment();
      }
      this.readCurrentUserInfo();
    }

    if ((this.loadedJudgments || []).length > 0) {
      this.judgments = [...this.loadedJudgments];
      this.setSelectedJudgment();
      return;
    }

    this.loading = true;
    const params = {
      isPega: this.formModel.isPega,
      objectKey: this.formModel.case.objectKey,
    };

    if (this.routeRequestObjectKey || this.requiredData?.isFromNonCaseParty) {
      this.getJudgementsByRequestObjectKey(this.routeRequestObjectKey ? this.routeRequestObjectKey : this.requiredData?.parentRequestObjectKey)
        .then((res) => {
          this.judgments = res.data;
          this.setSelectedJudgment();
        })
        .finally(() => (this.loading = false));
    }
    else {
      this.getCaseJudgments(params)
        .then((res) => {
          this.judgments = res.data;
          this.setSelectedJudgment();
        })
        .finally(() => (this.loading = false));
    }

  },
  methods: {
    ...mapActions([
      'getCaseJudgments',
      'uploadAttachment',
      'isValidApplicantForCommercialJudgement',
      'getJudgementsByRequestObjectKey',
    ]),
    reasonCheckChanged(index) {
      const selectedReason = this.formModel.cassationReasons[index];
      if (!selectedReason.checked) {
        this.formModel.cassationReasons[index].description = '';
      }
    },
    setSelectedJudgment() {
      const judgementCode = this.formModel && this.formModel.judgementCode;
      judgementCode && this.$set(this, 'selectedJudgment', (this.judgments || []).find(a => a.judgementCode == judgementCode) || {});

      if (judgementCode !== undefined) {
        if (this.lastValidatedJudgementCode === judgementCode) {
          return;
        }

        this.lastValidatedJudgementCode = judgementCode;
        this.loading = true;
        this.isValidApplicantForCommercialJudgement(judgementCode)
          .then(() => {
            this.$set(this.formModel, 'isApplicantEligibleToApply', true);
          })
          .catch(() => {
            this.$set(this.formModel, 'isApplicantEligibleToApply', false);
          })
          .finally(() => {
            this.loading = false;
          });
      } else {
        this.lastValidatedJudgementCode = null;
        this.$set(this.formModel, 'isApplicantEligibleToApply', undefined);
      }
    },
    getJudgementDeed() {
      this.showCaseJudgementModal = true;
    },
    closeCaseJudgementModal() {
      this.showCaseJudgementModal = false;
    },
    fileSelected({ selectedAttachment: attachment, selectedAttachmentKey }) {
      this.loading = true;
      const reader = new FileReader();
      reader.onloadend = () => {
        var b64 = reader.result.replace(/^data:.+;base64,/, '');

        this.uploadAttachment({
          classification: 0,
          documentMetadata: {
            caseId: 0,
            caseCode: 0,
            requestId: 0,
            category: attachment.category,
            fileCategory: attachment.category,
          },
          fileCategory: attachment.category,
          extension: attachment.extension,
          base64FileContent: b64,
        })
          .then(({ data }) => {
            this.$set(this.formModel, 'cassationFile',
              {
                fileData: {
                  attachement: data,
                  fileName: attachment.name,
                  fileType: attachment.fileObject.type,
                  sharePointId: data,
                  extension: attachment.extension,
                  fileCategory: attachment.category,
                  attachmentKey: selectedAttachmentKey,
                },
              }
            );
          })
          .catch(err => this.toaster.error(err.message))
          .finally(() => (this.loading = false));
      };
      reader.readAsDataURL(attachment.fileObject);
    },
    showDeleteObjectionNoteModal() {
      this.showDeleteObjectionNoteAttachment = true;
    },
    confirmDeleteObjectionNote() {
      this.$set(this.formModel, 'cassationFile', null);
      this.cancelDeleteObjectionNote();
    },
    cancelDeleteObjectionNote() {
      this.showDeleteObjectionNoteAttachment = false;
    },
    viewAttachment() {
      if (this.formModel.cassationFile.fileData.hide == true)
        this.showAlertModalForCriminalCases = true;
      else
        this.showViewAttachmentModal = true;
    },
    hideViewAttachmentModal() {
      this.showViewAttachmentModal = false;
    },

    readCurrentUserInfo() {
      if (this.userInfo) {
        const socialTypeId = this.userInfo.socialIdType;
        this.$set(this.formModel, 'socialIdType', socialTypeId);
        this.$set(this.formModel, 'socialId', this.userInfo.socialId);
        this.$set(this.formModel, 'firstName', this.userInfo.firstName);
        this.$set(this.formModel, 'fatherName', this.userInfo.fatherName);
        this.$set(this.formModel, 'grandFatherName', this.userInfo.grandFatherName);
        this.$set(this.formModel, 'familyName', this.userInfo.familyName);
        this.$set(this.formModel, 'dateOfBirth', this.userInfo.dateOfBirth);
        this.$set(this.formModel, 'nationalityCountryId', socialTypeId === socialIdentityTypeEnum.nationalId ? countriesEnum.saudiArabia : this.userInfo.nationalityCountryId);
        this.$set(this.formModel, 'sex', this.userInfo.gender);
        this.$set(this.formModel, 'isRetrievedFromMoi', true);
        this.$set(this.formModel, 'fullName', this.userInfo.fullName);
        this.$set(this.formModel, 'relatedPartyID', this.userInfo.relatedPartyID);
      } else {
        this.$set(this.formModel, 'socialIdType', null);
        this.$set(this.formModel, 'socialId', null);
        this.$set(this.formModel, 'firstName', null);
        this.$set(this.formModel, 'fatherName', null);
        this.$set(this.formModel, 'grandFatherName', null);
        this.$set(this.formModel, 'familyName', null);
        this.$set(this.formModel, 'dateOfBirth', null);
        this.$set(this.formModel, 'nationalityCountryId', null);
        this.$set(this.formModel, 'sex', null);
        this.$set(this.formModel, 'isRetrievedFromMoi', false);
        this.$set(this.formModel, 'fullName', null);
        this.$set(this.formModel, 'relatedPartyID', null);
      }
    },
  },
};

</script>

<style lang="scss" scoped>
.judgement-deed {
  display: flex;
  align-items: center;
  justify-content: center;
}

::v-deep .v-input--radio-group {
  .options-list {
    display: inline-grid !important;
  }
}

::v-deep .attachment-item {
  cursor: pointer;
}

.request-reasons-title {
  font-size: 18px;
}

.memorandum-challenge-alert-container {
  .alert-notification {
    display: block;
    color: #f6c645;
    font-size: 14px;
  }

  .message {
    font-size: 16px;
    color: #faad14;
  }
}

.objection-judgment-confirm-text {
  color: darkred;
  font-size: 1rem;
  margin-bottom: 0;
}

.objection-note-attachment {
  display: flex;

  .objection-judgment-confirm-text {
    margin-left: 10px;
  }

  .uplaod-attachments-button .img-actions {
    width: 100% !important;
  }
}

::v-deep .attachment-item {
  cursor: pointer;
}
.blackColor {
  color: black;
}
</style>